import { useState } from 'react';
import { scroller, animateScroll } from 'react-scroll';
import { UseFormReturn, Path, PathValue, DefaultValues } from 'react-hook-form';
import { useCookies } from 'react-cookie';

import { calculateProductScrollOffset } from '../../../helpers/selectPackage/calculateScrollOffsetForProduct';
import { createPackageToSubmit } from '../../../helpers/selectPackage/createPackageToSubmit';
import {
  findFirstPreselectedPrimaryProduct,
  getPreselectedPackageFormValues,
  defaultPackageFormValues,
} from '../../../helpers/selectPackage/getPreselectedPackageFormValues';
import { getFetchPricingProductsParams } from '../../../helpers/selectPackage/getFetchPricingProductsParams';
import { hasProductsInFormValues } from '../../../helpers/selectPackage/hasProductsInFormValues';
import { resetProductSingleIdsMap } from '../../../helpers/selectPackage/resetProductSingleIdsMap';

import { SiteTypes, UniqueIdentifier } from '../../../../../types';
import { PricingPackagesFormValues } from '../../../store/features/packages/types';
import {
  PricingProduct,
  SitePricingProductsData,
} from '../../../store/features/products/types';
import { useRootDispatch, useRootSelector } from '../../../../../store/hooks';
import { selectPageConfig } from '../../../../../store/features/pageConfig';
import {
  fetchSitePricingProducts,
  selectPricingBundledProductsMap,
} from '../../../store/features/products';
import {
  createSitePricingPackage,
  selectPricingPackagesCount,
} from '../../../store/features/packages';
import { canCookieBeSecure } from '../../../../../helpers';
import { selectSiteType } from '../../../../../store/features/siteType';
import { fetchCoursePricingOptions } from '../../../store/features/courseOptions';
import { useGetVIPCodeErrorMessages } from '../../discount/useGetVIPCodeErrorMessages';
import { usePaymentContext } from '../../../shared/PaymentProvider';
import { useDeviceDetect } from '../../../../../hooks';

const RESET_TIMEOUT_DURATION = 500;

export type SelectionProductId = UniqueIdentifier | null;

export interface UsePackageSelectionHandlersProps<T> {
  form: UseFormReturn<T>;
  preselectedProduct?: PricingProduct;
}

export interface ResetProductSelectionsParams {
  primaryProduct?: PricingProduct;
  siteOptionIndex?: number;
  delegateTypeId?: string;
  shouldSetPreselectedProductId?: boolean;
}

export function usePackageSelectionHandlers<
  T extends PricingPackagesFormValues,
>(props: UsePackageSelectionHandlersProps<T>) {
  const { form, preselectedProduct } = props;
  const { isDesktop } = useDeviceDetect();
  const dispatch = useRootDispatch();
  const [cookies, setCookie] = useCookies([
    'basket_id',
    'basket_order',
    'tracker_id',
  ]);
  const basketId = cookies.basket_id;
  const { setIsBasketFirstTimeAdded } = usePaymentContext();
  const { getVIPCodeErrorMessage } = useGetVIPCodeErrorMessages();
  const { type: siteType } = useRootSelector(selectSiteType);
  const pageConfig = useRootSelector(selectPageConfig);
  const bundledProductsMap = useRootSelector(selectPricingBundledProductsMap);
  const packagesCount = useRootSelector(selectPricingPackagesCount);
  const { cookieSitePath } = useRootSelector(selectPageConfig);

  const [selectedProductId, setSelectedProductId] =
    useState<SelectionProductId>(preselectedProduct?.id || null);

  const [addedToBasketProductId, setAddedToBasketProductId] =
    useState<SelectionProductId>(null);

  const handleChangeSelections = (
    defaultValues?: PricingPackagesFormValues,
  ) => {
    const formValues = defaultValues || form.getValues();

    return dispatch(
      fetchSitePricingProducts(
        getFetchPricingProductsParams({
          basketId,
          formValues,
          bundledProductsMap,
        }),
      ),
    );
  };

  const resetProductSelections = (
    resetParams: ResetProductSelectionsParams,
  ) => {
    const {
      primaryProduct,
      siteOptionIndex,
      delegateTypeId,
      shouldSetPreselectedProductId,
    } = resetParams;
    const { productsSelections, ...restPackageFormValues } = form.getValues();

    const initialPackageFormValues = {
      ...defaultPackageFormValues,
      ...restPackageFormValues,
      delegateTypeId,
      siteOptionIndex,
    };

    // 1. The first call of handleChangeSelections is needed to retrieve all appropriate products based on the delegate type
    // or site option index, in order to create default values with preselected products if they exist.
    handleChangeSelections(initialPackageFormValues).then(
      ({ payload, meta: { requestStatus } }) => {
        if (requestStatus === 'fulfilled') {
          const productsData = payload?.data as SitePricingProductsData['data'];

          const preselectedFormValues = getPreselectedPackageFormValues({
            productsData,
            selectedPrimaryProduct: primaryProduct,
            initialPackageFormValues,
          });
          const productSingleIdsMapResetValues = resetProductSingleIdsMap(
            // Add an empty string as the default value for each key in the previous productSingleIdsMap form values.
            // This ensures that all existing keys are reset to an empty string while preserving their structure.
            productsSelections.productSingleIdsMap,
          );

          form.reset({
            ...preselectedFormValues,
            productsSelections: {
              ...preselectedFormValues.productsSelections,
              productSingleIdsMap: {
                // the problem is that React Hook Form doesn't "deep merge" correctly objects when you call reset()
                ...productSingleIdsMapResetValues,
                ...preselectedFormValues.productsSelections.productSingleIdsMap,
              },
            },
          } as DefaultValues<T>);

          const shouldFetch = hasProductsInFormValues(
            preselectedFormValues.productsSelections,
          );

          if (shouldFetch) {
            // 2. The second call of handleChangeSelections is needed if preselectedFormValues contains
            // preselected products in productsSelections, to display the correct final price for the package.
            handleChangeSelections(preselectedFormValues);
          }

          if (shouldSetPreselectedProductId) {
            const preselectedPrimaryProduct =
              findFirstPreselectedPrimaryProduct(
                productsData.productTypesWithProducts,
              );

            setSelectedProductId(preselectedPrimaryProduct?.id || null);
          }
        }
      },
    );
  };

  const scrollToProduct = (product: PricingProduct, productIndex: number) => {
    const {
      elementsProperties: {
        multiLevelNavMenu: { height: navMenuHeight = 0 } = {},
      } = {},
    } = pageConfig;

    // Don't delete setTimeout, it's needed for proper work of scrollTo.
    // isDynamic as props of scrollTo doesn't work properly
    setTimeout(() => {
      scroller.scrollTo(product.id, {
        duration: 100,
        smooth: true,
        offset: calculateProductScrollOffset({
          navMenuHeight,
          productIndex,
          featuredLabelEnabled: product.featuredLabelEnabled,
        }),
      });
    }, 0);
  };

  const handleSelectProduct = (
    product: PricingProduct,
    productIndex: number,
  ) => {
    const { delegateTypeId, siteOptionIndex } = form.getValues();

    resetProductSelections({
      primaryProduct: product,
      siteOptionIndex,
      delegateTypeId,
    });
    setSelectedProductId(product.id || null);
    scrollToProduct(product, productIndex);
  };

  const handleUnselectProduct = (
    product: PricingProduct,
    productIndex: number,
  ) => {
    setSelectedProductId(null);
    scrollToProduct(product, productIndex);
  };

  const handleSuccessPackageCreation = (productId: UniqueIdentifier) => {
    setAddedToBasketProductId(productId);

    if (packagesCount === 0) {
      setIsBasketFirstTimeAdded(true);
    }

    if (siteType === SiteTypes.COURSE) {
      dispatch(fetchCoursePricingOptions({ basketId }));
    }

    setCookie('basket_order', basketId, {
      path: cookieSitePath,
      expires: new Date(Date.now() + 86400000),
      secure: canCookieBeSecure(),
    });

    setTimeout(() => {
      setAddedToBasketProductId(null);
      if (isDesktop) {
        animateScroll.scrollToTop({
          duration: 100,
          smooth: true,
        });
      }
      setSelectedProductId(null);
    }, RESET_TIMEOUT_DURATION);
  };

  const handleAddMultipleProductsToBasket = (
    values: PricingPackagesFormValues,
  ) => {
    const { productsSelections, vipCode, ...restValues } = values;

    dispatch(
      createSitePricingPackage({
        basketId,
        values: {
          ...restValues,
          packageSelection: createPackageToSubmit(
            productsSelections,
            bundledProductsMap,
          ),
        },
        queryParams: {
          promoCode: vipCode,
          tracker_id: cookies['tracker_id'],
        },
      }),
    ).then(({ meta: { requestStatus } }) => {
      if (requestStatus === 'fulfilled' && selectedProductId) {
        handleSuccessPackageCreation(selectedProductId);
      }
    });
  };

  const handleAddSingleProductToBasket = (productId: UniqueIdentifier) => {
    const { productsSelections, vipCode, ...restValues } = form.getValues();

    dispatch(
      createSitePricingPackage({
        basketId,
        values: {
          ...restValues,
          packageSelection: [{ productId }],
        },
        queryParams: {
          promoCode: vipCode,
          tracker_id: cookies['tracker_id'],
        },
      }),
    ).then(({ meta: { requestStatus } }) => {
      if (requestStatus === 'fulfilled') {
        handleSuccessPackageCreation(productId);
      }
    });
  };

  const handleValidateVipCode = (
    vipCodeValue: string,
    onSuccess: () => void,
    onError: (errorMessage: string) => void,
  ) => {
    const formValues = form.getValues();

    dispatch(
      fetchSitePricingProducts(
        getFetchPricingProductsParams({
          basketId,
          formValues,
          vipCodeForValidation: vipCodeValue,
          bundledProductsMap,
        }),
      ),
    ).then(({ payload, meta: { requestStatus } }) => {
      const { meta: { promotionCodeApplyResult } = {} } =
        payload as SitePricingProductsData;
      const { status, productNames, delegateTypesNames, success } =
        promotionCodeApplyResult || {};

      if (requestStatus === 'fulfilled' && success) {
        form.setValue(
          'vipCode' as Path<T>,
          vipCodeValue as PathValue<T, Path<T>>,
        );
        onSuccess();
      }

      if (!success) {
        onError(
          getVIPCodeErrorMessage({
            status,
            productNames,
            delegateTypesNames,
          }),
        );
      }
    });
  };

  return {
    handleAddMultipleProductsToBasket,
    handleAddSingleProductToBasket,
    handleSelectProduct,
    handleUnselectProduct,
    handleChangeSelections,
    handleValidateVipCode,
    resetProductSelections,
    setSelectedProductId,
    selectedProductId,
    addedToBasketProductId,
  };
}
